import React, { useState, useEffect } from 'react'
import { axiosInstance } from '../context/AuthContext'
import { useAuthContext } from '../hooks/useAuthContext'
import { useNavigate } from 'react-router-dom'
import Permissions from '../components/Permissions'
import CustomDialog from '../components/CustomDialog'
import Navbar from '../components/Navbar.js';
import AdminSectionLayout from '../components/AdminSectionLayout';

export default function InternalAdmin() {
  const { user } = useAuthContext()
  const navigate = useNavigate()

  const [users, setUsers] = useState([])
  const [page, setPage] = useState(1)
  const [showPermissions, setShowPermissions] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 10


  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({});

  const openDialog = (title, message, onConfirm) => {
    setDialogData({ title, message, onConfirm });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const disconnectUser = async (user) => {
    openDialog(
      'Confirmation',
      `Are you sure you want to disconnect ${user.userName ? user.userName : user.email}?`,
      async () => {
        try {
          await axiosInstance.delete(`/user/${user.id || user._id}`);
          fetchUsers();
        } catch (error) {
          console.error('Error disconnecting user: ', error);
        } finally {
          closeDialog();
        }
      }
    );
  };


  useEffect(() => {
    fetchUsers()
    if (user && !user.permissions.includes('admin')) {
      navigate('/home')
    }
  }, [page, searchQuery])

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(
        `/user?limit=${itemsPerPage}&page=${page}&search=${searchQuery}`,
      )
      setUsers(response.data.users)
      setTotalCount(response.data.totalUsersCount)
      setShowPermissions(Array(response.data.users.length).fill(false))
    } catch (error) {
      console.error('Error fetching users: ', error)
    }
  }

  const togglePermissions = (index) => {
    setShowPermissions((prevShowPermissions) => {
      const newShowPermissions = [...prevShowPermissions]
      newShowPermissions[index] = !newShowPermissions[index]
      return newShowPermissions
    })
  }

  const handleCancel = (index) => {
    setShowPermissions((prevShowPermissions) => {
      const newShowPermissions = [...prevShowPermissions]
      newShowPermissions[index] = false
      return newShowPermissions
    })
  }

  const handleStatusUpdate = async (userId, newStatus) => {
    console.log(userId, newStatus);
    try {
      await axiosInstance.post('/user/status', {
        userId,
        status: newStatus
      });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active':
        return 'bg-green-500 text-white';
      case 'pending':
        return 'bg-yellow-500 text-white';
      case 'disabled':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };


  return (
    <>
      <Navbar />
      <AdminSectionLayout
        title="Users"
        description="Manage portal users with this interface."
        actions={
          <input
            type="text"
            placeholder="Search by name or email"
            className="w-full sm:w-80 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        }
      >
        <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 text-sm overflow-hidden">
          <div className="max-h-[80vh] overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed">
              <thead className="sticky top-0 bg-white z-10 border-b border-gray-100">
                <tr>
                  <th className="pl-4 sm:pl-6 py-2 text-left">Permissions</th>
                  <th className="pl-4 sm:pl-6 py-2 text-left">Username</th>
                  <th className="pl-4 sm:pl-6 py-2 text-left">Email</th>
                  <th className="py-2 text-center w-24">Status</th>
                  <th className="py-2 text-center w-36 sm:w-40">Edit</th>
                  <th className="py-2 text-center w-36 sm:w-40">Action</th>
                </tr>
              </thead>
              <tbody className="text-base">
                {users.map((user, index) => (
                  <tr key={user.id}>
                    <td className="px-4 sm:px-6 py-2 border-t text-center">
                      <div className="flex flex-wrap gap-1">
                          {user.permissions && user.permissions.map((permission, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded"
                            >
                              {permission}
                            </span>
                          ))}
                        </div>
                    </td>
                    <td className="px-4 sm:px-10 py-2 border-t border-l">
                      {user.firstName && user.firstName}{' '}
                      {user.lastName && user.lastName}
                    </td>
                    <td className="px-4 py-2 border-t border-l break-all">{user.email}</td>
                    <td className="px-4 py-2 border-t border-l text-center w-24">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(user.status)}`}>
                        {user.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-t border-l w-32">
                      <div className="flex flex-col gap-2">
                       
                        {showPermissions[index] ? (
                          <Permissions
                            key={`permissions-${user.id}`}
                            onCancel={() => handleCancel(index)}
                            user={user}
                            fetchUsers={fetchUsers}
                          />
                        ) : (
                          <button
                            className="px-2 py-1 text-xs sm:text-sm font-semibold text-blue-700 bg-transparent border border-blue-500 rounded hover:bg-blue-500 hover:text-white whitespace-nowrap m-w-[30px]"
                            onClick={() => togglePermissions(index)}
                          >
                            Edit Permissions
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 border-t border-l">
                      <select
                        value={user.status || 'pending'}
                        onChange={(e) => handleStatusUpdate(user.id || user._id, e.target.value)}
                        className={`px-3 py-2 text-sm font-semibold rounded border w-full ${
                          user.status === 'active' ? 'bg-green-100 text-green-800 border-green-500' :
                          user.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-500' :
                          'bg-red-100 text-red-800 border-red-500'
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        <CustomDialog
          isOpen={isDialogOpen}
          title={dialogData.title}
          message={dialogData.message}
          onConfirm={dialogData.onConfirm}
          onCancel={closeDialog}
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * itemsPerPage + 1} -{' '}
            {Math.min(page * itemsPerPage, totalCount)} of {totalCount} users
          </p>
          <div className="flex">
            <button
              onClick={() => setPage((prevPage) => prevPage - 1)}
              disabled={page === 1}
              className={`px-3 py-1.5 text-sm mr-2 ${page === 1
                  ? 'text-gray-400 bg-gray-200'
                  : 'text-white bg-blue-500 '
                } rounded`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((prevPage) => prevPage + 1)}
              disabled={users.length < itemsPerPage}
              className={`px-3 py-1.5 text-sm mr-2 ${users.length < itemsPerPage
                  ? 'text-gray-400 bg-gray-200'
                  : 'text-white bg-blue-500 '
                } rounded`}
            >
              Next
            </button>
          </div>
        </div>
      </AdminSectionLayout>
    </>
  )
}
